import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Loader, MessageCircle } from "lucide-react";
import { AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import { loadFacebookSdk, onEmbeddedSignupEvent } from "../../utils/facebookSdk";
import "./whatsapp-dashboard.scss";

/**
 * /whatsapp/connect/embedded — where `FB.login` actually runs.
 *
 * The Facebook JS SDK will only run on a domain listed in the app console's
 * **Allowed Domains for the JavaScript SDK**, and that list takes no wildcards.
 * Calling `FB.login` from the institute's own page therefore means one console
 * entry per subdomain and per custom domain, forever — the exact trap the OAuth
 * relay was built to escape, reintroduced by a different door.
 *
 * So Embedded Signup is launched from *here*, on the canonical host. The
 * institute's tab navigates to this page, Facebook only ever sees this one
 * domain, and when the dialog returns a code the page hands it back to the
 * institute's own origin — an ordinary browser navigation Facebook has no part
 * in and nothing needs to whitelist.
 *
 * A tab, not a popup: `FB.login` opens a window of its own, so a popup here
 * would leave the customer facing three at once.
 *
 * Deliberately mirrors `WhatsAppConnectRelay`:
 *
 *   • it never exchanges the code — that needs a session, and this page has none
 *   • it never decides the return origin — the backend signed that into `state`
 *   • it is **public by necessity**: the admin's token lives in `localStorage` on
 *     their own origin, so `ProtectedRoute` would bounce this page to a login
 *     screen on the wrong domain
 */
const WhatsAppEmbeddedSignup = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("");
  const [busy, setBusy] = useState(false);
  const resolvedRef = useRef(false);

  /* ── Resolve the signed state into an origin + the signup config ────────── */
  useEffect(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;

    const state = searchParams.get("state") || "";
    if (!state) {
      setError("This setup link is not valid. Go back and try connecting again.");
      return;
    }

    (async () => {
      try {
        const { data } = await apiClient.post("/whatsapp/connect/resolve-state", { state });
        if (!data?.origin || !data?.appId) {
          setError("This setup link is not valid. Go back and try connecting again.");
          return;
        }
        if (!data.configId) {
          // Without a configuration id this is not Embedded Signup. Say so
          // rather than opening a dialog that cannot work.
          setError("Guided setup is not configured. Go back and use the standard connect.");
          return;
        }
        setConfig(data);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Could not start setup. Go back and try connecting again."
        );
      }
    })();
  }, [searchParams]);

  /**
   * Started by a click, never automatically — and that is not a style choice.
   *
   * `FB.login` opens a window, and a browser blocks one that does not descend
   * from a user gesture. When it does the SDK simply never calls back: no error,
   * no callback, a spinner that turns forever. The button is what supplies the
   * gesture.
   */
  const start = useCallback(() => {
    if (!config || busy) return;
    setBusy(true);
    setError("");

    let lastEvent = null;
    const unsubscribe = onEmbeddedSignupEvent((payload) => {
      lastEvent = payload;
      const current = payload?.data?.current_step;
      if (current) setStep(String(current).replace(/_/g, " ").toLowerCase());
    });

    const fail = (message) => {
      unsubscribe();
      setBusy(false);
      setError(message);
    };

    loadFacebookSdk(config.appId, config.graphVersion)
      .then((FB) => {
        // Plain function, never async — the SDK rejects a promise-returning
        // callback with "Expression is of type asyncfunction, not function".
        FB.login(
          (response) => {
            unsubscribe();
            const code = response?.authResponse?.code;
            if (!code) {
              const current = lastEvent?.data?.current_step;
              fail(
                lastEvent?.data?.error_message ||
                  (current
                    ? `Setup was closed at the "${String(current).replace(/_/g, " ").toLowerCase()}" step.`
                    : "Setup was cancelled before it finished.")
              );
              return;
            }
            forward(config.origin, code);
          },
          {
            config_id: config.configId,
            response_type: "code",
            override_default_response_type: true,
            extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
          }
        );
      })
      .catch((err) => {
        fail(err?.message || "Facebook could not be loaded. Check any ad blocker, then try again.");
      });
  }, [config, busy]);

  /**
   * This page owns the tab, so a dead end here strands the customer on a domain
   * that is not theirs. Every exit leads back to the institute's own dashboard.
   */
  const goBack = () => {
    if (config?.origin) window.location.replace(new URL("/whatsapp", config.origin).toString());
  };

  /**
   * `replace`, not `assign` — the authorization code is in this URL, and a
   * history entry on the canonical host would keep it reachable by Back long
   * after it was spent.
   */
  const forward = (origin, code) => {
    const url = new URL("/whatsapp/connect/callback", origin);
    url.searchParams.set("code", code);
    url.searchParams.set("flow", "embedded_signup");
    window.location.replace(url.toString());
  };

  const render = () => {
    if (error) {
      return (
        <>
          <div className="wa-connect-empty__icon wa-connect-empty__icon--error">
            <AlertCircle size={40} />
          </div>
          <h3>Setup did not finish</h3>
          <p className="wa-text-muted">{error}</p>
          <div className="wa-inline-actions">
            {config && <AppButton onClick={start}>Try again</AppButton>}
            {config?.origin && (
              <AppButton variant="outline" onClick={goBack}>Back to dashboard</AppButton>
            )}
          </div>
        </>
      );
    }

    if (!config) {
      return (
        <>
          <div className="wa-connect-empty__icon wa-connect-empty__icon--loading">
            <Loader size={40} className="wa-spin" />
          </div>
          <h3>Preparing setup…</h3>
        </>
      );
    }

    if (busy) {
      return (
        <>
          <div className="wa-connect-empty__icon wa-connect-empty__icon--loading">
            <Loader size={40} className="wa-spin" />
          </div>
          <h3>Setting up WhatsApp</h3>
          <p className="wa-text-muted">
            {step || "Continue in the Facebook window."}
          </p>
        </>
      );
    }

    return (
      <>
        <div className="wa-connect-empty__icon">
          <MessageCircle size={40} />
        </div>
        <h3>Set up WhatsApp Business</h3>
        <p className="wa-text-muted">
          Facebook will guide you through creating your WhatsApp Business account,
          verifying your phone number and choosing a display name.
        </p>
        <div className="wa-inline-actions">
          <AppButton onClick={start}>Continue with Facebook</AppButton>
          <AppButton variant="outline" onClick={goBack}>Cancel</AppButton>
        </div>
      </>
    );
  };

  return (
    <div className="wa-connect-relay">
      <div className="wa-connect-empty">{render()}</div>
    </div>
  );
};

export default WhatsAppEmbeddedSignup;
